import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Component that adds structured data (schema.org) to improve SEO
 * Contains bilingual (English/Arabic) schema markup that changes based on current language
 */
const StructuredData = () => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  
  // Create review schema items with sample testimonials
  const reviewSchemaItems = [
    {
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": isEnglish ? "Sarah Johnson" : "سارة جونسون"
      },
      "reviewBody": isEnglish 
        ? "I've had the best dental experience at National Medical Elite. The staff is professional and the results are amazing!" 
        : "حظيت بأفضل تجربة لطب الأسنان في النخبة الوطنية الطبية. الموظفون محترفون والنتائج مذهلة!"
    },
    {
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": isEnglish ? "Ahmed Al-Dosari" : "أحمد الدوسري"
      },
      "reviewBody": isEnglish 
        ? "The orthodontic treatment I received was excellent. Dr. Ramez and his team are truly skilled professionals." 
        : "كان علاج تقويم الأسنان الذي تلقيته ممتازًا. الدكتور رامز وفريقه هم متخصصون مهرة حقًا."
    },
    {
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": isEnglish ? "Fatima Al-Qahtani" : "فاطمة القحطاني"
      },
      "reviewBody": isEnglish 
        ? "The cosmetic dentistry changed my smile completely. Very satisfied with the professionalism and care." 
        : "غير طب الأسنان التجميلي ابتسامتي تمامًا. راضية جدًا عن الاحترافية والرعاية."
    }
  ];
  
  // Structured data for dental clinic with bilingual information
  const clinicSchema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "name": isEnglish ? "National Medical Elite" : "النخبة الوطنية الطبية",
    "alternateName": isEnglish ? "NME Dental Clinic" : "عيادة أسنان النخبة",
    "url": window.location.origin + (isEnglish ? "/en" : "/ar"),
    "logo": `${window.location.origin}/logo192.png`,
    "image": [
      `${window.location.origin}/clinic-exterior.jpg`,
      `${window.location.origin}/clinic-reception.jpg`
    ],
    "telephone": "+966138333003",
    "email": "national.elite.clinic@gmail.com",
    "description": isEnglish 
      ? "Premium dental clinic offering comprehensive dental services including cosmetic dentistry, implants, orthodontics and pediatric dentistry in Dammam, Saudi Arabia." 
      : "عيادة أسنان متميزة تقدم خدمات طب الأسنان الشاملة بما في ذلك طب الأسنان التجميلي وزراعة الأسنان وتقويم الأسنان وطب أسنان الأطفال في الدمام، المملكة العربية السعودية.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": isEnglish ? "Omar Ibn Al-Khattab Street" : "شارع عمر بن الخطاب",
      "addressLocality": isEnglish ? "Dammam" : "الدمام",
      "addressRegion": isEnglish ? "Eastern Province" : "المنطقة الشرقية",
      "postalCode": "32424",
      "addressCountry": isEnglish ? "Saudi Arabia" : "المملكة العربية السعودية"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "26.4416",
      "longitude": "50.1156"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"],
        "opens": "09:00",
        "closes": "22:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday"],
        "opens": "09:00",
        "closes": "21:00"
      }
    ],
    "priceRange": "$$",
    "currenciesAccepted": "SAR",
    "paymentAccepted": isEnglish ? "Cash, Credit Card" : "نقدي، بطاقة ائتمان",
    "areaServed": isEnglish 
      ? ["Dammam", "Khobar", "Dhahran", "Eastern Province"] 
      : ["الدمام", "الخبر", "الظهران", "المنطقة الشرقية"],
    "availableLanguage": {
      "@type": "Language",
      "name": isEnglish ? "English, Arabic" : "العربية، الإنجليزية"
    },
    "sameAs": [
      "https://www.facebook.com/people/%D8%A7%D9%84%D9%86%D8%AE%D8%A8%D8%A9-%D8%A7%D9%84%D9%88%D8%B7%D9%86%D9%8A%D8%A9/pfbid0Zin9PPWJTsVyBwxcK4GAqJy5Q4uj8JSMtuWzj7fHSRaTxXr2jWNrfTzH2UqxNQsHl/?mibextid=wwXIfr&rdid=KeMQPGmIGXR0GFyH&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F16U7iChQ8c%2F%3Fmibextid%3DwwXIfr",
      "https://www.instagram.com/national.elite.medical/?igsh=MTZmdGw3YXVtcjllOA%3D%3D#",
      "https://www.tiktok.com/@national_elite?is_from_webapp=1&sender_device=pc",
      "https://www.snapchat.com/add/nationalelitecl?sender_web_id=d5c9fc03-6e62-42d8-9ecc-e18623198f37&device_type=desktop&is_copy_url=true"
    ],
    // Medical specialty keywords
    "specialty": isEnglish 
      ? ["Cosmetic Dentistry", "Dental Implants", "Orthodontics", "Pediatric Dentistry", "Teeth Whitening", "Dental Veneers"]
      : ["طب الأسنان التجميلي", "زراعة الأسنان", "تقويم الأسنان", "طب أسنان الأطفال", "تبييض الأسنان", "قشور الأسنان"],
    // Services offered with bilingual keywords
    "medicalSpecialty": isEnglish 
      ? "Dental Care, Orthodontics, Cosmetic Dentistry, Implantology"
      : "رعاية الأسنان، تقويم الأسنان، طب الأسنان التجميلي، زراعة الأسنان",
    // Additional keywords in both languages
    "keywords": isEnglish
      ? "best dental clinic Dammam, dental implants Saudi Arabia, teeth whitening Eastern Province, cosmetic dentistry Saudi Arabia, orthodontist Dammam"
      : "أفضل عيادة أسنان في الدمام، زراعة أسنان المملكة العربية السعودية، تبييض الأسنان المنطقة الشرقية، طب الأسنان التجميلي المملكة العربية السعودية، تقويم أسنان الدمام",
    
    // Add aggregate rating based on reviews
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "87",
      "bestRating": "5",
      "worstRating": "1"
    },
    
    // Include reviews from testimonialData
    "review": reviewSchemaItems
  };

  // Create FAQ schema for common dental questions
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": isEnglish 
          ? "What dental services do you offer?" 
          : "ما هي خدمات طب الأسنان التي تقدمونها؟",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": isEnglish 
            ? "We offer comprehensive dental services including cosmetic dentistry, dental implants, orthodontics, pediatric dentistry, teeth whitening, root canal therapy, and more." 
            : "نقدم خدمات طب الأسنان الشاملة بما في ذلك طب الأسنان التجميلي، وزراعة الأسنان، وتقويم الأسنان، وطب أسنان الأطفال، وتبييض الأسنان، وعلاج جذور الأسنان، والمزيد."
        }
      },
      {
        "@type": "Question",
        "name": isEnglish 
          ? "What are your clinic hours?" 
          : "ما هي ساعات عمل العيادة؟",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": isEnglish 
            ? "We are open Sunday through Thursday from 9 AM to 10 PM, and Saturday from 9 AM to 9 PM." 
            : "نحن مفتوحون من الأحد إلى الخميس من الساعة 9 صباحًا حتى 10 مساءً، ويوم السبت من الساعة 9 صباحًا حتى 9 مساءً."
        }
      },
      {
        "@type": "Question",
        "name": isEnglish 
          ? "Do you accept insurance?" 
          : "هل تقبلون التأمين الصحي؟",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": isEnglish 
            ? "Yes, we accept most major insurance providers. Please contact us to verify your specific insurance coverage." 
            : "نعم، نقبل معظم شركات التأمين الرئيسية. يرجى الاتصال بنا للتحقق من تغطية التأمين الخاصة بك."
        }
      }
    ]
  };

  return (
    <>
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(clinicSchema) }}
      />
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
};

export default StructuredData;
